import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../App';
import { useIsMobile } from '../utils';
import { RESELLER_PERM_LIST } from '../resellerPerms';
import { EditClientModal } from '../components/client/EditClientModal';
import { NewClientModal } from './Clients';
import { UserModal } from './Users';

// #210 etapa 2, parte 2 — página «Resellers» (sólo admin): las empresas reseller con su plan, su
// consumo contra la cuota, sus usuarios y su techo de permisos. El detalle de cada empresa lista sus
// usuarios y sus clientes, y lleva a los modales de siempre (editar empresa con el techo, alta de usuario).

const btn = (color) => ({ background: 'transparent', border: `1px solid ${color}`, color, padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', letterSpacing: 1, whiteSpace: 'nowrap' });
const th = { textAlign: 'left', padding: '10px 14px', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', fontWeight: 400, borderBottom: '1px solid var(--border)' };
const td = { padding: '12px 14px', fontSize: 13, borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };
const gb = (n) => `${Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 1 })} GB`;

function NoInfraBadge() {
  return (
    <span style={{ padding: '1px 6px', background: 'rgba(255,77,109,0.12)', color: 'var(--red)', border: '1px solid var(--red)', borderRadius: 4, fontSize: 10, fontFamily: 'var(--mono)', letterSpacing: 1, whiteSpace: 'nowrap' }}>
      SIN INFRAESTRUCTURA
    </span>
  );
}

function PlanCell({ c }) {
  return c.plan ? <span>{c.plan.name}</span> : <NoInfraBadge />;
}

function UsersCell({ c }) {
  return (
    <span>
      {c.user_count}{' '}
      <span data-alert={c.admin_count === 0 ? 'true' : 'false'} style={{ color: c.admin_count === 0 ? 'var(--red)' : 'var(--muted)', fontSize: 11 }}>
        ({c.admin_count} administran)
      </span>
    </span>
  );
}

function StorageCell({ c }) {
  if (c.storage_quota_gb == null) {
    return <span>{gb(c.storage_used_gb)} <span style={{ color: 'var(--muted)', fontSize: 11 }}>· sin tope</span></span>;
  }
  const pct = c.storage_pct ?? 0;
  const color = pct >= 100 ? 'var(--red)' : pct >= 80 ? 'var(--yellow)' : 'var(--accent)';
  return (
    <div style={{ minWidth: 150 }}>
      <div style={{ fontSize: 12 }}>{gb(c.storage_used_gb)} / {gb(c.storage_quota_gb)} <span style={{ color }}>({pct}%)</span></div>
      <div style={{ height: 4, background: 'var(--border)', marginTop: 4 }}>
        <div style={{ height: 4, width: `${Math.min(100, pct)}%`, background: color }} />
      </div>
    </div>
  );
}

function CompanyDetail({ detail, onEdit, onNewUser, isMobile }) {
  const navigate = useNavigate();
  const permLabels = (perms) => RESELLER_PERM_LIST.filter(p => perms?.[p.key]).map(p => p.label);
  const sub = { fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', margin: '16px 0 8px' };
  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: 16 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={onEdit} style={btn('var(--accent)')}>EDITAR EMPRESA</button>
        <button onClick={onNewUser} style={btn('var(--yellow)')}>+ USUARIO</button>
      </div>

      <div style={sub}>Usuarios reseller ({detail.users.length})</div>
      {detail.users.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>Sin usuarios.</div>
      ) : detail.users.map(u => (
        <div key={u.id} style={{ border: '1px solid var(--border)', padding: '10px 12px', marginBottom: 8, opacity: u.active ? 1 : 0.6 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ fontWeight: 600 }}>{u.display_name || u.email}</span>
              {u.display_name && <span style={{ color: 'var(--muted)', marginLeft: 8, fontSize: 12 }}>{u.email}</span>}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {u.is_account_admin && <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--accent)', border: '1px solid var(--accent)', padding: '1px 6px' }}>ADMINISTRA LA CUENTA</span>}
              <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: u.active ? 'var(--green)' : '#9ca3af', border: `1px solid ${u.active ? 'var(--green)' : '#9ca3af'}`, padding: '1px 6px' }}>{u.active ? 'ACTIVO' : 'INACTIVO'}</span>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, fontFamily: 'var(--mono)' }}>
            Último ingreso: {u.last_login ? new Date(u.last_login).toLocaleString('es-AR') : 'nunca'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
            Permisos efectivos: {permLabels(u.permissions).join(', ') || 'ninguno'}
          </div>
        </div>
      ))}

      <div style={sub}>Clientes ({detail.clients.length})</div>
      {detail.clients.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>Sin clientes.</div>
      ) : detail.clients.map(c => (
        <div key={c.id} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 16, alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', border: '1px solid var(--border)', padding: '10px 12px', marginBottom: 8, opacity: c.active ? 1 : 0.6 }}>
          <button onClick={() => navigate(`/clients/${c.id}`)} style={{ background: 'none', border: 'none', color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 13, padding: 0, textAlign: 'left' }}>
            {c.name}
          </button>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            {c.active ? 'Activo' : 'Inactivo'} · {c.plan ? c.plan.name : 'Sin plan'} · {gb(c.storage_used_gb)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Resellers() {
  const isMobile = useIsMobile();
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [openId, setOpenId]       = useState(null);
  const [detail, setDetail]       = useState(null);
  const [editClient, setEditClient] = useState(null);
  const [userModal, setUserModal] = useState(null);
  const [newModal, setNewModal]   = useState(false);
  const [error, setError]         = useState('');
  const [toast, setToast]         = useState('');

  const loadDetail = useCallback(async (id) => {
    try { setDetail(await api.getResellerCompany(id)); } catch (e) { setError(e.message); }
  }, []);

  const load = useCallback(async () => {
    try {
      const [list, p] = await Promise.all([api.getResellerCompanies(showInactive), api.getPlans()]);
      setCompanies(list);
      setPlans(p);
      setError('');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [showInactive]);

  // Sólo el admin pide algo: para el resto la ruta ya redirige y, por las dudas, esto no corre.
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  useEffect(() => { if (isAdmin && openId) loadDetail(openId); else setDetail(null); }, [isAdmin, openId, loadDetail]);

  const refresh = () => { load(); if (openId) loadDetail(openId); };
  const toggle = (id) => setOpenId(cur => (cur === id ? null : id));

  const openEdit = async () => {
    try { setEditClient(await api.getClient(openId)); } catch (e) { setError(e.message); }
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>CARGANDO...</div>;

  const detailBlock = (c) => (openId === c.id && detail && detail.id === c.id) && (
    <CompanyDetail detail={detail} isMobile={isMobile} onEdit={openEdit}
      onNewUser={() => setUserModal({ role: 'reseller', client_id: c.id })} />
  );

  return (
    <div>
      {newModal && <NewClientModal plans={plans} resellers={[]} isAdmin initialReseller
        onClose={() => setNewModal(false)} onCreated={() => { refresh(); setToast('Empresa reseller creada'); }} />}
      {editClient && <EditClientModal client={editClient} plans={plans} onClose={() => setEditClient(null)} onSaved={refresh} />}
      {userModal && <UserModal user={userModal} clients={companies.map(c => ({ id: c.id, name: c.name, is_reseller: true }))}
        onClose={() => setUserModal(null)}
        onSaved={(created) => {
          refresh();
          const mail = created?.welcome_email;
          setToast(mail && !mail.sent ? `Usuario creado, pero el mail con el enlace no salió (${mail.error || 'error de envío'}).` : 'Usuario creado: se le envió un mail para definir su contraseña');
        }} />}

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-end', justifyContent: 'space-between', marginBottom: 24, gap: isMobile ? 12 : 0 }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 20 }}>Resellers</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{companies.filter(c => c.active).length} empresas activas</div>
        </div>
        <button onClick={() => setNewModal(true)} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '8px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700, letterSpacing: 1, whiteSpace: 'nowrap' }}>
          + NUEVA EMPRESA RESELLER
        </button>
      </div>

      {toast && <div style={{ marginBottom: 12, padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12, background: 'rgba(0,229,160,0.1)', border: '1px solid var(--green)', color: 'var(--green)' }}>✓ {toast}</div>}
      {error && <div style={{ marginBottom: 12, padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12, background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)' }}>✕ {error}</div>}

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 12, color: 'var(--muted)', cursor: 'pointer', userSelect: 'none', fontFamily: 'var(--mono)' }}>
        <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} style={{ cursor: 'pointer', accentColor: 'var(--accent)' }} />
        Mostrar también empresas dadas de baja
      </label>

      {companies.length === 0 ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
          No hay empresas reseller todavía.
        </div>
      ) : isMobile ? (
        <div>
          {companies.map(c => (
            <div key={c.id} style={{ marginBottom: 8 }}>
              <div onClick={() => toggle(c.id)} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '14px 16px', cursor: 'pointer', opacity: c.active ? 1 : 0.7 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {c.name}
                  {!c.active && <span style={{ padding: '1px 6px', background: 'rgba(156,163,175,0.15)', color: '#9ca3af', border: '1px solid #9ca3af', borderRadius: 4, fontSize: 10, fontFamily: 'var(--mono)', letterSpacing: 1 }}>INACTIVA</span>}
                </div>
                <div style={{ display: 'grid', gap: 6, fontSize: 12 }}>
                  <div><span style={{ color: 'var(--muted)' }}>Plan: </span><PlanCell c={c} /></div>
                  <div><span style={{ color: 'var(--muted)' }}>Usuarios: </span><UsersCell c={c} /></div>
                  <div><span style={{ color: 'var(--muted)' }}>Clientes: </span>{c.client_count}</div>
                  <div><span style={{ color: 'var(--muted)' }}>Storage: </span><StorageCell c={c} /></div>
                  <div><span style={{ color: 'var(--muted)' }}>Descuento: </span>{c.discount_pct}%</div>
                  <div><span style={{ color: 'var(--muted)' }}>Techo: </span>{c.ceiling_on}/{c.ceiling_total}</div>
                </div>
              </div>
              {detailBlock(c)}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Empresa', 'Plan', 'Usuarios', 'Clientes', 'Storage', 'Descuento', 'Techo'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <FragmentRows key={c.id}>
                  <tr onClick={() => toggle(c.id)} style={{ cursor: 'pointer', opacity: c.active ? 1 : 0.7, background: openId === c.id ? 'var(--bg)' : undefined }}>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {c.name}
                      {!c.active && <span style={{ marginLeft: 8, padding: '1px 6px', background: 'rgba(156,163,175,0.15)', color: '#9ca3af', border: '1px solid #9ca3af', borderRadius: 4, fontSize: 10, fontFamily: 'var(--mono)', letterSpacing: 1 }}>INACTIVA</span>}
                    </td>
                    <td style={td}><PlanCell c={c} /></td>
                    <td style={td}><UsersCell c={c} /></td>
                    <td style={td}>{c.client_count}</td>
                    <td style={td}><StorageCell c={c} /></td>
                    <td style={td}>{c.discount_pct}%</td>
                    <td style={td}>{c.ceiling_on}/{c.ceiling_total}</td>
                  </tr>
                  {openId === c.id && detail && detail.id === c.id && (
                    <tr><td colSpan={7} style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>{detailBlock(c)}</td></tr>
                  )}
                </FragmentRows>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Un <tbody>-friendly fragmento con key (React.Fragment sin importar el default).
function FragmentRows({ children }) { return <>{children}</>; }
